import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { clientsService } from '@/features/clients/services/clients.service';
import { ClientDetail } from '@/features/clients/components/ClientDetail';
import { ClientForm } from '@/features/clients/components/ClientForm';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const Route = createFileRoute('/_authenticated/clients/$id')({
  component: ClientDetailsPage,
});

function ClientDetailsPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { data: client, isLoading } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clientsService.getClient(id),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof clientsService.updateClient>[1]) =>
      clientsService.updateClient(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', id] });
      queryClient.invalidateQueries({ queryKey: ['clients-list'] });
      toast({ title: 'Cliente atualizado com sucesso' });
      setEditDialogOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => clientsService.deleteClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients-list'] });
      toast({ title: 'Cliente removido com sucesso' });
      navigate({ to: '/clients' });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" aria-label="Voltar" onClick={() => navigate({ to: '/clients' })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">{client?.name || 'Cliente'}</h1>
            {client?.clientType && (
              <Badge
                variant="outline"
                className={
                  client.clientType === 'internal'
                    ? 'border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950'
                    : 'border-emerald-400 text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950'
                }
              >
                {client.clientType === 'internal' ? 'Interno' : 'Externo'}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">Detalhes do cliente</p>
        </div>
        <div className="ml-auto">
          <Button
            variant="destructive"
            loading={deleteMutation.isPending}
            onClick={async () => {
              const confirmed = await confirm({
                title: 'Excluir este cliente?',
                description: 'Esta ação não poderá ser desfeita.',
                confirmLabel: 'Excluir cliente',
                destructive: true,
              });
              if (!confirmed) return;
              deleteMutation.mutate();
            }}
          >
            Excluir cliente
          </Button>
        </div>
      </div>

      <ClientDetail
        client={client}
        isLoading={isLoading}
        onEdit={() => setEditDialogOpen(true)}
      />

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent size="lg" busy={updateMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
          </DialogHeader>
          <ClientForm
            initialData={client}
            onSubmit={(data) => updateMutation.mutate(data)}
            onCancel={() => setEditDialogOpen(false)}
            isLoading={updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
